import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ArrowRightStartOnRectangleIcon
} from '@heroicons/react/24/outline';

const GoogleAnalyticsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.84 2.998v17.958a3.065 3.065 0 0 1-3.044 3.044 3.065 3.065 0 0 1-3.044-3.044V2.998a3.065 3.065 0 0 1 3.044-3.044 3.065 3.065 0 0 1 3.044 3.044zM13.544 9.3v11.7a3.012 3.012 0 0 1-3.044 2.998 3.012 3.012 0 0 1-3.044-2.998V9.3a3.012 3.012 0 0 1 3.044-2.998A3.012 3.012 0 0 1 13.544 9.3zM4.204 17.952a3.044 3.044 0 1 1-3.044 3.044 3.065 3.065 0 0 1 3.044-3.044z"/>
  </svg>
);

export { GoogleAnalyticsIcon };

export default function GoogleAnalyticsSource({ companyId, onSuccess, onBack }) {
  const [searchParams] = useSearchParams();

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [googleAccount, setGoogleAccount] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Property selection
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');

  // Dimensions & Metrics
  const [availableDimensions, setAvailableDimensions] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState(new Set());
  const [selectedMetrics, setSelectedMetrics] = useState(new Set());
  const [dimSearchTerm, setDimSearchTerm] = useState('');
  const [metricSearchTerm, setMetricSearchTerm] = useState('');

  // Date range
  const [dateRangeType, setDateRangeType] = useState('relative');
  const [relativeDays, setRelativeDays] = useState(30);
  const [fixedStartDate, setFixedStartDate] = useState('');
  const [fixedEndDate, setFixedEndDate] = useState('');

  // Preview
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);

  // Form
  const [name, setName] = useState('');

  // UI
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    checkExistingGoogleAuth();
  }, []);

  useEffect(() => {
    const googleAuth = searchParams.get('googleAuth');
    const tokenData = searchParams.get('tokenData');
    const error = searchParams.get('error');

    if (error) {
      toast.error(`Google authentication failed: ${error}`);
      setIsCheckingAuth(false);
      return;
    }

    if (googleAuth === 'success' && tokenData) {
      try {
        const decoded = JSON.parse(atob(tokenData));
        setTokens(decoded);
        setIsAuthenticated(true);
        setStep(2);
        window.history.replaceState({}, '', window.location.pathname);
        toast.success('Google account connected successfully!');
        setIsCheckingAuth(false);
        checkExistingGoogleAuth(true);
      } catch (e) {
        console.error('Failed to decode token data:', e);
        toast.error('Failed to process authentication');
        setIsCheckingAuth(false);
      }
    }
  }, [searchParams]);

  const checkExistingGoogleAuth = async (skipStepChange = false) => {
    try {
      const result = await api.get('/data-sources/google/auth-status');

      if (result.data.connected) {
        setTokens(result.data.tokens);
        setGoogleAccount(result.data.account);
        setIsAuthenticated(true);
        if (!skipStepChange) {
          setStep(2);
        }
      }
    } catch (error) {
      console.warn('Could not check Google auth status:', error.message);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsAuthenticating(true);
    try {
      const result = await api.post('/data-sources/google/auth', {
        companyId,
        returnUrl: window.location.href,
        sourceType: 'googleanalytics'
      });
      window.location.href = result.data.authUrl;
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.error || 'Failed to start Google authentication');
      setIsAuthenticating(false);
    }
  };

  const handleSwitchAccount = async () => {
    try {
      await api.post('/data-sources/google/disconnect');
    } catch (err) {
      console.warn('Failed to disconnect Google account:', err.message);
    }

    setTokens(null);
    setGoogleAccount(null);
    setIsAuthenticated(false);
    setProperties([]);
    setSelectedProperty(null);
    setAvailableDimensions([]);
    setAvailableMetrics([]);
    setSelectedDimensions(new Set());
    setSelectedMetrics(new Set());
    setPreviewData([]);
    setPreviewColumns([]);
    setName('');
    setStep(1);

    setIsAuthenticating(true);
    try {
      const result = await api.post('/data-sources/google/auth', {
        companyId,
        returnUrl: window.location.href,
        sourceType: 'googleanalytics'
      });
      const authUrl = new URL(result.data.authUrl);
      authUrl.searchParams.set('prompt', 'consent select_account');
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.error || 'Failed to start Google authentication');
      setIsAuthenticating(false);
    }
  };

  // Load properties when entering step 2
  useEffect(() => {
    if (step === 2 && isAuthenticated && tokens?.accessToken && properties.length === 0) {
      loadProperties();
    }
  }, [step, isAuthenticated, tokens]);

  const loadProperties = async () => {
    setIsLoading(true);
    try {
      const result = await api.post('/data-sources/google-analytics/properties', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken
      });
      setProperties(result.data.properties || []);
    } catch (error) {
      console.error('Load properties error:', error);
      toast.error('Failed to load Google Analytics properties');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectProperty = async (property) => {
    setSelectedProperty(property);
    setName(property.displayName);
    setIsLoading(true);
    try {
      const result = await api.post('/data-sources/google-analytics/metadata', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        propertyId: property.propertyId
      });
      setAvailableDimensions(result.data.dimensions || []);
      setAvailableMetrics(result.data.metrics || []);

      // Pre-select common defaults
      const defaultDims = ['date'];
      const defaultMetrics = ['sessions', 'activeUsers', 'screenPageViews'];
      setSelectedDimensions(new Set(
        result.data.dimensions
          .filter(d => defaultDims.includes(d.apiName))
          .map(d => d.apiName)
      ));
      setSelectedMetrics(new Set(
        result.data.metrics
          .filter(m => defaultMetrics.includes(m.apiName))
          .map(m => m.apiName)
      ));

      setStep(3);
    } catch (error) {
      console.error('Load metadata error:', error);
      toast.error('Failed to load property dimensions and metrics');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    if (selectedDimensions.size === 0 || selectedMetrics.size === 0) {
      toast.error('Please select at least one dimension and one metric');
      return;
    }
    setIsLoading(true);
    try {
      const dateRange = dateRangeType === 'relative'
        ? { rangeType: 'relative', relativeDays }
        : { startDate: fixedStartDate, endDate: fixedEndDate };

      const result = await api.post('/data-sources/google-analytics/preview', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        propertyId: selectedProperty.propertyId,
        dimensions: Array.from(selectedDimensions),
        metrics: Array.from(selectedMetrics),
        dateRange
      });
      setPreviewColumns(result.data.columns || []);
      setPreviewData(result.data.preview || []);
      setStep(4);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.error || 'Failed to preview report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    setIsCreating(true);
    try {
      const connectionConfig = {
        gaPropertyId: selectedProperty.propertyId,
        gaPropertyName: selectedProperty.displayName,
        gaDimensions: Array.from(selectedDimensions),
        gaMetrics: Array.from(selectedMetrics),
        gaDateRange: dateRangeType === 'relative'
          ? { rangeType: 'relative', relativeDays }
          : { rangeType: 'fixed', startDate: fixedStartDate, endDate: fixedEndDate },
        googleAccessToken: tokens.accessToken,
        googleRefreshToken: tokens.refreshToken,
        googleTokenExpiry: tokens.expiry
      };

      const result = await api.post('/data-sources', {
        name,
        type: 'googleanalytics',
        companyId,
        connectionConfig,
        schemaInfo: {
          tables: [{
            name: selectedProperty.displayName,
            columns: previewColumns
          }]
        }
      });

      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };

  const filteredProperties = useMemo(() => {
    if (!propertySearchTerm) return properties;
    const q = propertySearchTerm.toLowerCase();
    return properties.filter(p =>
      p.displayName.toLowerCase().includes(q) ||
      p.accountName.toLowerCase().includes(q) ||
      p.propertyId.toLowerCase().includes(q)
    );
  }, [properties, propertySearchTerm]);

  const filteredDimensions = useMemo(() => {
    if (!dimSearchTerm) return availableDimensions;
    const q = dimSearchTerm.toLowerCase();
    return availableDimensions.filter(d =>
      d.apiName.toLowerCase().includes(q) ||
      d.uiName.toLowerCase().includes(q) ||
      (d.category || '').toLowerCase().includes(q)
    );
  }, [availableDimensions, dimSearchTerm]);

  const filteredMetrics = useMemo(() => {
    if (!metricSearchTerm) return availableMetrics;
    const q = metricSearchTerm.toLowerCase();
    return availableMetrics.filter(m =>
      m.apiName.toLowerCase().includes(q) ||
      m.uiName.toLowerCase().includes(q) ||
      (m.category || '').toLowerCase().includes(q)
    );
  }, [availableMetrics, metricSearchTerm]);

  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowPathIcon className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Connect' },
          { num: 2, label: 'Property' },
          { num: 3, label: 'Configure' },
          { num: 4, label: 'Preview' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num
                ? 'bg-orange-600 text-white'
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 3 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>

      {/* Step 1: Google Authentication */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <GoogleAnalyticsIcon className="w-8 h-8 text-orange-500" />
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Connect to Google Analytics
            </h3>
          </div>

          <div className="text-center py-8">
            <GoogleAnalyticsIcon className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              Sign in with Google
            </h4>
            <p className="text-surface-600 dark:text-surface-400 mb-6 max-w-md mx-auto">
              Connect your Google account to import data from Google Analytics 4 properties you have access to.
            </p>

            <button
              onClick={handleGoogleAuth}
              disabled={isAuthenticating}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-sm hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
            >
              {isAuthenticating ? (
                <ArrowPathIcon className="w-5 h-5 animate-spin text-surface-600" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="font-medium text-surface-900 dark:text-white">
                {isAuthenticating ? 'Connecting...' : 'Sign in with Google'}
              </span>
            </button>
          </div>

          <div className="flex justify-between pt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={onBack} className="btn-secondary">Back</button>
          </div>
        </div>
      )}

      {/* Step 2: Select Property */}
      {step === 2 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GoogleAnalyticsIcon className="w-8 h-8 text-orange-500" />
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Select GA4 Property
              </h3>
            </div>
          </div>

          {/* Connected account banner */}
          <div className="flex items-center justify-between p-3 mb-6 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700">
            <div className="flex items-center gap-3">
              {googleAccount?.picture ? (
                <img src={googleAccount.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <GoogleAnalyticsIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </div>
              )}
              <div>
                {googleAccount?.email ? (
                  <>
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {googleAccount.name || googleAccount.email}
                    </p>
                    {googleAccount.name && (
                      <p className="text-xs text-surface-500">{googleAccount.email}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm font-medium text-surface-900 dark:text-white">
                    Google Account Connected
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleSwitchAccount}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-md hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors"
            >
              <ArrowRightStartOnRectangleIcon className="w-3.5 h-3.5" />
              Switch Account
            </button>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <ArrowPathIcon className="w-8 h-8 text-orange-500 animate-spin" />
              <p className="text-surface-600 dark:text-surface-400">Loading properties...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-8">
              <ExclamationCircleIcon className="w-12 h-12 text-surface-400 mx-auto mb-3" />
              <p className="text-surface-600 dark:text-surface-400">
                No Google Analytics 4 properties found for this account.
              </p>
              <p className="text-sm text-surface-500 mt-1">
                Make sure you have access to at least one GA4 property.
              </p>
            </div>
          ) : (
            <>
              {properties.length > 5 && (
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={propertySearchTerm}
                    onChange={(e) => setPropertySearchTerm(e.target.value)}
                    placeholder="Search properties..."
                    className="input pl-9 py-2 text-sm w-full"
                  />
                </div>
              )}
              <div className="grid gap-2 max-h-96 overflow-y-auto">
                {filteredProperties.map((prop) => (
                  <button
                    key={prop.propertyId}
                    onClick={() => handleSelectProperty(prop)}
                    className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-orange-300 dark:hover:border-orange-700 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                      <GoogleAnalyticsIcon className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-surface-900 dark:text-white truncate">
                        {prop.displayName}
                      </p>
                      <p className="text-sm text-surface-500 truncate">
                        {prop.accountName} &middot; {prop.propertyId.replace('properties/', '')}
                      </p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-surface-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={onBack} className="btn-secondary">Back</button>
          </div>
        </div>
      )}

      {/* Step 3: Configure Report */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-1">
              <GoogleAnalyticsIcon className="w-6 h-6 text-orange-500" />
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Configure Report
              </h3>
            </div>
            <p className="text-sm text-surface-500 mb-6">
              {selectedProperty?.displayName} &middot; Select the dimensions and metrics to import.
            </p>

            {/* Dimensions */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Dimensions ({selectedDimensions.size} selected)
                </h4>
              </div>
              {availableDimensions.length > 10 && (
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={dimSearchTerm}
                    onChange={(e) => setDimSearchTerm(e.target.value)}
                    placeholder="Search dimensions..."
                    className="input pl-9 py-2 text-sm w-full"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {filteredDimensions.map((dim) => (
                  <label
                    key={dim.apiName}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedDimensions.has(dim.apiName)
                        ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                    title={dim.description}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDimensions.has(dim.apiName)}
                      onChange={() => {
                        const next = new Set(selectedDimensions);
                        next.has(dim.apiName) ? next.delete(dim.apiName) : next.add(dim.apiName);
                        setSelectedDimensions(next);
                      }}
                      className="rounded text-orange-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate">
                        {dim.uiName}
                      </p>
                      <p className="text-xs text-surface-500 truncate">{dim.apiName}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Metrics ({selectedMetrics.size} selected)
                </h4>
              </div>
              {availableMetrics.length > 10 && (
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={metricSearchTerm}
                    onChange={(e) => setMetricSearchTerm(e.target.value)}
                    placeholder="Search metrics..."
                    className="input pl-9 py-2 text-sm w-full"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {filteredMetrics.map((metric) => (
                  <label
                    key={metric.apiName}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedMetrics.has(metric.apiName)
                        ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                    title={metric.description}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMetrics.has(metric.apiName)}
                      onChange={() => {
                        const next = new Set(selectedMetrics);
                        next.has(metric.apiName) ? next.delete(metric.apiName) : next.add(metric.apiName);
                        setSelectedMetrics(next);
                      }}
                      className="rounded text-orange-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate">
                        {metric.uiName}
                      </p>
                      <p className="text-xs text-surface-500 truncate">{metric.apiName}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div>
              <h4 className="font-medium text-surface-900 dark:text-white mb-3">Date Range</h4>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateRangeType"
                    value="relative"
                    checked={dateRangeType === 'relative'}
                    onChange={() => setDateRangeType('relative')}
                    className="text-orange-600"
                  />
                  <span className="text-sm text-surface-900 dark:text-white">Relative</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateRangeType"
                    value="fixed"
                    checked={dateRangeType === 'fixed'}
                    onChange={() => setDateRangeType('fixed')}
                    className="text-orange-600"
                  />
                  <span className="text-sm text-surface-900 dark:text-white">Fixed</span>
                </label>
              </div>

              {dateRangeType === 'relative' ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-surface-600 dark:text-surface-400">Last</span>
                  <input
                    type="number"
                    value={relativeDays}
                    onChange={(e) => setRelativeDays(Math.max(1, parseInt(e.target.value) || 1))}
                    className="input w-24 text-sm"
                    min={1}
                    max={365}
                  />
                  <span className="text-sm text-surface-600 dark:text-surface-400">days</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={fixedStartDate}
                    onChange={(e) => setFixedStartDate(e.target.value)}
                    className="input text-sm"
                  />
                  <span className="text-sm text-surface-500">to</span>
                  <input
                    type="date"
                    value={fixedEndDate}
                    onChange={(e) => setFixedEndDate(e.target.value)}
                    className="input text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Info note about GA4 limits */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm">
            <ExclamationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-blue-700 dark:text-blue-300">
              GA4 reports return aggregated data (not raw events). You can select up to 9 dimensions and 10 metrics per report.
            </p>
          </div>

          <div className="flex justify-between">
            <button onClick={() => {
              setSelectedProperty(null);
              setAvailableDimensions([]);
              setAvailableMetrics([]);
              setSelectedDimensions(new Set());
              setSelectedMetrics(new Set());
              setStep(2);
            }} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handlePreview}
              disabled={isLoading || selectedDimensions.size === 0 || selectedMetrics.size === 0}
              className="btn-primary"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Loading Preview...
                </>
              ) : (
                'Preview Report'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Preview & Create */}
      {step === 4 && (
        <div className="space-y-6">
          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={selectedProperty?.displayName}
            />
          </div>

          {/* Data Preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Preview
              </h4>
              <span className="text-sm text-surface-500">
                Showing {Math.min(previewData.length, 10)} of {previewData.length} preview rows
              </span>
            </div>

            {previewData.length === 0 ? (
              <div className="text-center py-8 text-surface-500">
                No data returned for the selected configuration.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {previewColumns.map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                          <span className="ml-1 text-xs text-surface-400">({col.type})</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {previewColumns.map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate">
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !name.trim()}
              className="btn-primary"
            >
              {isCreating ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Create Data Source
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
